import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, Truck, CheckCircle, Clock, AlertCircle, 
  Filter, Download, Eye, RefreshCw, Search, X, 
  ChevronLeft, ChevronRight, ShoppingBag, DollarSign,
  Calendar, User, Phone, MapPin, CreditCard
} from 'lucide-react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const API_URL = process.env.REACT_APP_API_URL;

function CustomerOrders({ isMobile, user, setCurrentPage }) {
  const [orders, setOrders] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [filters, setFilters] = useState({});
  const [currentPageNum, setCurrentPageNum] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalSpent: 0,
    pendingOrders: 0,
    deliveredOrders: 0
  });

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API_URL}/index.php`, {
        apiType: "getOrders",
        userId: user?.id,
        page: currentPageNum,
        limit: itemsPerPage,
        filters: filters
      });
      
      if (response.data.success) {
        setOrders(response.data.orders);
        setStats({
          totalOrders: response.data.orders.length,
          totalSpent: response.data.orders.reduce((sum, order) => sum + (parseFloat(order.total) || 0), 0),
          pendingOrders: response.data.orders.filter(o => o.status === 'pending' || o.status === 'processing').length,
          deliveredOrders: response.data.orders.filter(o => o.status === 'delivered').length
        });
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      // Mock data for demo
      const mockOrders = Array.from({ length: 12 }, (_, i) => ({
        id: `ORD-${1000 + i}`,
        customer_name: user?.name || 'Customer',
        total: 45.99 + (i * 15),
        status: ['pending', 'processing', 'delivered', 'cancelled'][i % 4],
        created_at: new Date(Date.now() - i * 86400000).toISOString().split('T')[0],
        payment_status: ['paid', 'pending', 'paid', 'failed'][i % 4],
        item_count: 2 + (i % 5),
        delivery_option: i % 2 === 0 ? 'delivery' : 'pickup',
        phone: '+263 78' + (3907172 + i),
        address: 'Zvido Business Centre, Chitungwiza',
        items: [
          { name: 'Premium Pork Leg', quantity: 2, price: 22.99, total: 45.98 },
          { name: 'Pork Shoulder', quantity: 1, price: 15.99, total: 15.99 }
        ]
      }));
      
      setOrders(mockOrders);
      setStats({
        totalOrders: mockOrders.length,
        totalSpent: mockOrders.reduce((sum, order) => sum + order.total, 0),
        pendingOrders: mockOrders.filter(o => o.status === 'pending' || o.status === 'processing').length,
        deliveredOrders: mockOrders.filter(o => o.status === 'delivered').length
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPageNum(1);
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
  };

  const handleReorder = async (order) => {
    try {
      // Simulate reorder API call
      setTimeout(() => {
        alert('Order recreated successfully! You can review it in your cart.');
      }, 1000);
    } catch (error) {
      console.error('Reorder error:', error);
      alert('Failed to reorder. Please try again.');
    }
  };

  const handleExport = (exportType) => {
    try {
      const exportData = orders.map((order, index) => ({
        'SN': index + 1,
        'Order ID': order.id,
        'Date': order.created_at,
        'Customer': order.customer_name,
        'Total': `$${order.total.toFixed(2)}`,
        'Status': order.status,
        'Payment Status': order.payment_status,
        'Items': order.item_count,
        'Delivery': order.delivery_option
      }));

      if (exportType === 'excel') {
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Orders");
        XLSX.writeFile(wb, `my_orders_${new Date().toISOString().split('T')[0]}.xlsx`);
      } else if (exportType === 'pdf') {
        const { jsPDF } = window.jspdf;
        const doc = new jsPDF();
        
        doc.setFontSize(16);
        doc.text('My Orders - Sir Henry\'s Butchery', 14, 15);
        doc.setFontSize(10);
        doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 22);
        
        const tableData = exportData.map(item => [
          item.SN,
          item['Order ID'],
          item.Date,
          item.Total,
          item.Status,
          item['Payment Status'],
          item.Items
        ]);

        doc.autoTable({
          startY: 25,
          head: [['SN', 'Order ID', 'Date', 'Total', 'Status', 'Payment Status', 'Items']],
          body: tableData,
          theme: 'grid',
          styles: { fontSize: 8 },
          headStyles: { fillColor: [26, 26, 26] }
        });

        doc.save(`my_orders_${new Date().toISOString().split('T')[0]}.pdf`);
      }
      
      alert(`Orders exported successfully as ${exportType.toUpperCase()}!`);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export orders.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'from-green-500 to-emerald-600';
      case 'processing': return 'from-blue-500 to-cyan-600';
      case 'pending': return 'from-amber-500 to-orange-600';
      case 'cancelled': return 'from-red-500 to-pink-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered': return <CheckCircle className="w-4 h-4" />;
      case 'processing': return <Package className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'cancelled': return <AlertCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const filteredOrders = orders.filter(order => {
    return Object.keys(filters).every(key => {
      if (!filters[key]) return true;
      return String(order[key]).toLowerCase().includes(filters[key].toLowerCase());
    });
  });

  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const paginatedOrders = filteredOrders.slice(
    (currentPageNum - 1) * itemsPerPage,
    currentPageNum * itemsPerPage
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-600 flex items-center justify-center">
              <ShoppingBag className="w-6 h-6 text-white" />
            </div>
            My Orders
          </h2>
          <p className="text-gray-400">Track and manage all your purchases</p>
        </div>
        
        <div className="flex items-center gap-3">
          <select 
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            className="px-4 py-2 bg-gray-800 border border-gray-700 text-white rounded-lg"
          >
            <option value="grid">Grid View</option>
            <option value="list">List View</option>
          </select>
          
          <button 
            onClick={fetchOrders}
            className="p-2 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Orders', value: stats.totalOrders, icon: ShoppingBag, color: 'blue' },
          { label: 'Total Spent', value: `$${stats.totalSpent.toFixed(2)}`, icon: DollarSign, color: 'green' },
          { label: 'Pending Orders', value: stats.pendingOrders, icon: Clock, color: 'amber' },
          { label: 'Delivered', value: stats.deliveredOrders, icon: CheckCircle, color: 'emerald' }
        ].map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-xl bg-gradient-to-r ${getStatusColor(stat.color)} bg-opacity-20`}>
                <stat.icon className={`w-6 h-6 text-${stat.color}-400`} />
              </div>
            </div>
            <div className="text-3xl font-bold text-white mb-2">{stat.value}</div>
            <div className="text-sm text-gray-400">{stat.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Search and Filters */}
      <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search orders by ID or status..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
            />
          </div>
          
          <div className="flex gap-3">
            <select
              value={filters.status || ''}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
            
            <input
              type="date"
              placeholder="Filter by date..."
              value={filters.created_at || ''}
              onChange={(e) => handleFilterChange('created_at', e.target.value)}
              className="px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg"
            />
          </div>
        </div>
        
        <div className="flex space-x-3">
          <button 
            onClick={() => handleExport('excel')}
            className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export Excel
          </button>
          <button 
            onClick={() => handleExport('pdf')}
            className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-lg flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export PDF
          </button>
        </div>
      </div>

      {/* Order Details Modal */}
      <AnimatePresence>
        {selectedOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            >
              <div className="p-8">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h3 className="text-2xl font-bold text-white">Order Details</h3>
                    <p className="text-gray-400">#{selectedOrder.id}</p>
                  </div>
                  <button 
                    onClick={() => setSelectedOrder(null)}
                    className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Order Summary */}
                  <div className="lg:col-span-2 space-y-6">
                    {/* Order Items */}
                    <div className="bg-gray-900/50 rounded-xl p-6">
                      <h4 className="text-lg font-bold text-white mb-4">Order Items</h4>
                      <div className="space-y-4">
                        {selectedOrder.items?.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                                <Package className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <div className="font-medium text-white">{item.name}</div>
                                <div className="text-sm text-gray-400">Qty: {item.quantity}</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-white">${item.total.toFixed(2)}</div>
                              <div className="text-sm text-gray-400">${item.price} each</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Customer Info */}
                    <div className="bg-gray-900/50 rounded-xl p-6">
                      <h4 className="text-lg font-bold text-white mb-4">Customer Information</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-gray-400">Customer Name</label>
                          <div className="flex items-center gap-2 mt-1">
                            <User className="w-4 h-4 text-gray-500" />
                            <span className="text-white">{selectedOrder.customer_name}</span>
                          </div>
                        </div>
                        <div>
                          <label className="text-sm text-gray-400">Phone Number</label>
                          <div className="flex items-center gap-2 mt-1">
                            <Phone className="w-4 h-4 text-gray-500" />
                            <span className="text-white">{selectedOrder.phone}</span>
                          </div>
                        </div>
                        <div>
                          <label className="text-sm text-gray-400">Delivery Option</label>
                          <div className="flex items-center gap-2 mt-1">
                            <Truck className="w-4 h-4 text-gray-500" />
                            <span className="text-white capitalize">{selectedOrder.delivery_option}</span>
                          </div>
                        </div>
                        <div>
                          <label className="text-sm text-gray-400">Order Date</label>
                          <div className="flex items-center gap-2 mt-1">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <span className="text-white">{selectedOrder.created_at}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Order Status */}
                  <div className="space-y-6">
                    <div className="bg-gray-900/50 rounded-xl p-6">
                      <h4 className="text-lg font-bold text-white mb-4">Order Status</h4>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400">Status</span>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r ${getStatusColor(selectedOrder.status)}`}>
                            {selectedOrder.status}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400">Payment</span>
                          <span className={`px-2 py-1 rounded-full text-xs ${selectedOrder.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                            {selectedOrder.payment_status}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400">Items</span>
                          <span className="text-white">{selectedOrder.item_count}</span>
                        </div>
                        <div className="pt-4 border-t border-gray-800">
                          <div className="flex items-center justify-between text-lg font-bold">
                            <span className="text-gray-400">Total</span>
                            <span className="text-white">${selectedOrder.total.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <button
                        onClick={() => handleReorder(selectedOrder)}
                        className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-bold hover:shadow-lg transition-all"
                      >
                        Reorder
                      </button>
                      <button
                        onClick={() => setSelectedOrder(null)}
                        className="w-full py-3 border border-gray-700 text-gray-300 rounded-xl font-bold hover:bg-gray-800 transition-colors"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Orders Display */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-gray-800 to-gray-900 rounded-full flex items-center justify-center">
            <Package className="w-10 h-10 text-gray-600" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">No Orders Yet</h3>
          <p className="text-gray-400 mb-6">You haven't placed any orders yet</p>
          <button 
            onClick={() => setCurrentPage('shop')}
            className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-bold hover:shadow-lg transition-all"
          >
            Start Shopping
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginatedOrders.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              onViewDetails={handleViewDetails}
              onReorder={handleReorder}
              getStatusColor={getStatusColor}
              getStatusIcon={getStatusIcon}
            />
          ))}
        </div>
      ) : (
        <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-900/50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Order ID</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Date</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Total</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Items</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {paginatedOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-900/30">
                    <td className="px-6 py-4">
                      <div className="font-bold text-white">{order.id}</div>
                    </td>
                    <td className="px-6 py-4 text-gray-300">{order.created_at}</td>
                    <td className="px-6 py-4">
                      <div className="text-lg font-bold text-white">${order.total.toFixed(2)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r ${getStatusColor(order.status)} flex items-center gap-1 w-fit`}>
                        {getStatusIcon(order.status)}
                        {order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-300">{order.item_count}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewDetails(order)}
                          className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleReorder(order)}
                          className="p-2 text-green-400 hover:bg-green-500/10 rounded-lg"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {filteredOrders.length > 0 && (
        <div className="flex flex-col md:flex-row items-center justify-between px-6 py-4 bg-gray-900/50 rounded-2xl border border-gray-800">
          <div className="text-sm text-gray-400 mb-4 md:mb-0">
            Showing {(currentPageNum - 1) * itemsPerPage + 1} to {Math.min(currentPageNum * itemsPerPage, filteredOrders.length)} of {filteredOrders.length} orders
          </div>
          <div className="flex items-center gap-4">
            <button 
              className="p-2 bg-gray-800 border border-gray-700 rounded-lg disabled:opacity-50"
              disabled={currentPageNum === 1}
              onClick={() => setCurrentPageNum(prev => prev - 1)}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm font-medium text-white">
              Page {currentPageNum} of {totalPages}
            </span>
            <button 
              className="p-2 bg-gray-800 border border-gray-700 rounded-lg disabled:opacity-50"
              disabled={currentPageNum === totalPages}
              onClick={() => setCurrentPageNum(prev => prev + 1)}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function OrderCard({ order, onViewDetails, onReorder, getStatusColor, getStatusIcon }) {
  return (
    <motion.div
      whileHover={{ y: -5 }}
      className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 overflow-hidden"
    >
      <div className="p-6">
        {/* Order Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-white">#{order.id}</h3>
            <p className="text-sm text-gray-400">{order.created_at}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r ${getStatusColor(order.status)} flex items-center gap-1`}>
            {getStatusIcon(order.status)}
            {order.status}
          </span>
        </div>

        {/* Order Details */}
        <div className="space-y-3 mb-6">
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Items</span>
            <span className="text-white font-bold">{order.item_count}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Total</span>
            <span className="text-xl font-bold text-white">${order.total.toFixed(2)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Payment</span>
            <span className={`text-sm ${order.payment_status === 'paid' ? 'text-green-400' : 'text-yellow-400'}`}>
              {order.payment_status}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => onViewDetails(order)}
            className="py-2 bg-gray-800 text-gray-300 hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <Eye className="w-4 h-4" />
            Details
          </button>
          <button
            onClick={() => onReorder(order)}
            className="py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition-all flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Reorder
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default CustomerOrders;